import clsx from 'clsx';
import { type ComponentPropsWithRef, type ForwardedRef, type ReactNode } from 'react';
import { Button, type ButtonProps, Link, type LinkProps } from 'react-aria-components';

import { Skeleton, TextSkeleton } from '../Skeleton';
import { Tooltip, type TooltipProps } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';
import { DivButton, type DivButtonProps } from './DivButton';

interface SidebarItemHookProps {
    label?: string;
    icon?: IconDefinition;
    children?: ReactNode;
    className?: string;
    isActive?: boolean;
    iconOnly?: boolean;
    rounded?: boolean;
    outlined?: boolean;
    size?: 'medium' | 'large';
    tooltip?: Omit<TooltipProps, 'children'>;
    elementType: 'button' | 'anchor' | 'tree-item' | 'div';
}

export function useSidebarItem<T extends SidebarItemHookProps>(props: T) {
    const {
        icon,
        size = 'medium',
        label,
        className,
        elementType,
        isActive,
        iconOnly,
        rounded,
        outlined,
        children,
        ...rest
    } = props;
    if (!icon && iconOnly) {
        throw new Error('iconOnly prop requires an icon prop');
    }
    return {
        itemProps: {
            'data-active': isActive ? '' : undefined,
            children: (
                <>
                    {icon && <SidebarItemIcon icon={icon} size={size} />}
                    {label && !iconOnly ? <span className="truncate">{label}</span> : null}
                    {children}
                </>
            ),
            className: clsx(
                'group/sidebar-item flex items-center gap-2 bg-neutral-surface-subtle px-(--px) py-(--py) text-ui-base text-base',
                size === 'large' && 'font-medium',
                rounded || iconOnly
                    ? 'rounded-full [--px:0.375rem] [--py:0.375rem]'
                    : 'rounded-sm [--px:0.375rem] [--py:0.3125rem]',
                outlined && 'border border-solid border-base',
                elementType !== 'div' &&
                    clsx(
                        'data-[pressed=true]:bg-neutral-ui-active',
                        '-outline-offset-1 outline-neutral data-[focus-visible]:outline-1',
                        isActive ? 'bg-neutral-ui-active' : 'hover:bg-neutral-ui-hover'
                    ),
                elementType === 'anchor' || elementType === 'button'
                    ? 'group/sidebar-item-std aria-expanded:bg-neutral-ui-active'
                    : undefined,
                className
            ),
            'aria-label': iconOnly ? label : undefined,
            ...rest,
        },
    };
}

function getSidebarItemTooltipProps(
    props: Pick<SidebarItemHookProps, 'iconOnly' | 'label' | 'tooltip'>
) {
    if (props.tooltip) {
        return props.tooltip;
    }

    if (props.iconOnly && props.label) {
        return { label: props.label };
    }

    return null;
}

export interface SidebarItemProps
    extends
        Omit<ComponentPropsWithRef<'div'>, 'children' | 'className'>,
        Omit<SidebarItemHookProps, 'elementType'> {}

export function SidebarItem({ ref, ...props }: SidebarItemProps) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'div' });
    const element = <div ref={ref} {...itemProps} />;
    const tooltipProps = getSidebarItemTooltipProps(props);
    if (tooltipProps) {
        return <Tooltip {...tooltipProps}>{element}</Tooltip>;
    }
    return element;
}

export interface SidebarButtonProps
    extends
        Omit<ButtonProps, 'children' | 'className'>,
        Omit<SidebarItemHookProps, 'elementType'> {}

export function SidebarButton({
    ref,
    ...props
}: SidebarButtonProps & { ref?: ForwardedRef<HTMLButtonElement> }) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'button' });
    const element = <Button ref={ref} {...itemProps} />;
    const isExpanded = props['aria-expanded'] === 'true' || props['aria-expanded'] === true;
    const tooltipProps = getSidebarItemTooltipProps(props);
    if (tooltipProps) {
        return (
            <Tooltip {...tooltipProps} disabled={tooltipProps.disabled || isExpanded}>
                {element}
            </Tooltip>
        );
    }
    return element;
}

export interface SidebarLinkProps
    extends Omit<LinkProps, 'children' | 'className'>, Omit<SidebarItemHookProps, 'elementType'> {}

export function SidebarLink({
    ref,
    ...props
}: SidebarLinkProps & { ref?: ForwardedRef<HTMLAnchorElement> }) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'anchor' });
    const element = <Link ref={ref} {...itemProps} />;
    const tooltipProps = getSidebarItemTooltipProps(props);
    if (tooltipProps) {
        return <Tooltip {...tooltipProps}>{element}</Tooltip>;
    }
    return element;
}

export interface SidebarItemIconProps {
    icon: IconDefinition;
    size?: 'medium' | 'large';
}

export function SidebarItemIcon(props: SidebarItemIconProps) {
    const { icon, size = 'medium' } = props;
    return renderIcon(icon, {
        size: { medium: 16, large: 20 }[size],
        className: clsx(
            'shrink-0 text-muted group-hover/sidebar-item:text-base group-aria-expanded/sidebar-item-std:text-base group-aria-[current=page]/sidebar-item:text-base group-data-[active]/sidebar-item:text-base group-data-[pressed=true]/sidebar-item:text-base'
        ),
    });
}

export interface SidebarItemButtonProps extends Omit<
    DivButtonProps,
    'children' | 'preventFocusOnPress'
> {
    icon: IconDefinition;
}

export function SidebarItemButton({
    ref,
    ...props
}: SidebarItemButtonProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    const { icon, ...rest } = props;
    return (
        <DivButton
            ref={ref}
            {...rest}
            preventFocusOnPress
            className={clsx(
                'peer relative flex shrink-0 rounded-xs p-0.5 text-muted opacity-0 transition',
                'group-hover/sidebar-item:opacity-100 group-data-[focus-visible]/sidebar-item:opacity-100 hover:bg-(--neutral-a3) hover:text-base focus:opacity-100 aria-expanded:bg-(--neutral-a4) aria-expanded:opacity-100 data-[pressed=true]:bg-(--neutral-a4)',
                'data-[focus-visible]:focus-ring-inset',
                rest.className
            )}
        >
            {renderIcon(icon, { size: 14 })}
        </DivButton>
    );
}

export function SidebarItemSkeleton() {
    return (
        <div className="flex items-center gap-2">
            <Skeleton shape="rect" className="size-6" />
            <TextSkeleton className="text-ui-heading-medium">Title of the item</TextSkeleton>
        </div>
    );
}
